import React from 'react';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import ContactForm from '../components/shared/ContactForm';
import { companyInfo } from '../data/mock';

const ContactPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section id="contatti-section" className="pt-32 pb-16 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-amber-600 font-semibold text-sm uppercase tracking-wider mb-4">
              Contatti
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-800 mb-6 leading-tight">
              Siamo qui per te
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Hai bisogno di informazioni sui nostri servizi o vuoi richiedere un preventivo gratuito? 
              Compila il modulo sottostante oppure contattaci direttamente. Ti risponderemo il prima possibile.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="bg-white border-slate-100 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-1">Telefono</h3>
                      <a 
                        href={`tel:${companyInfo.phone}`}
                        className="text-slate-600 hover:text-amber-600 transition-colors"
                      >
                        {companyInfo.phone}
                      </a>
                      <p className="text-slate-500 text-sm mt-1">Lun-Ven: 8:00-18:00</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-100 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-1">Email</h3>
                      <a 
                        href={`mailto:${companyInfo.email}`}
                        className="text-slate-600 hover:text-blue-600 transition-colors"
                      >
                        {companyInfo.email}
                      </a>
                      <p className="text-slate-500 text-sm mt-1">Risposta entro 24h</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-100 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-1">Indirizzo</h3>
                      <p className="text-slate-600">{companyInfo.address}</p>
                      <p className="text-slate-500 text-sm mt-1">Su appuntamento</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-100 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-1">Orari</h3>
                      <p className="text-slate-600">{companyInfo.hours}</p>
                      <p className="text-slate-500 text-sm mt-1">Domenica chiuso</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="bg-white border-slate-100 shadow-xl">
                <CardContent className="p-6 sm:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <Send className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-slate-800">Scrivici</h2>
                      <p className="text-slate-500">Compila il form e ti ricontatteremo</p>
                    </div>
                  </div>
                  <ContactForm />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-800 mb-4">Dove siamo</h2>
            <p className="text-slate-600">Operiamo in tutta la provincia di Trapani</p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-xl h-[400px] bg-slate-200">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3185.9080367456!2d12.7885!3d37.6779!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1319e4c6aef5c9d1%3A0x9c1b6c0c5c5c5c5c!2sVia%20Tagliata%2C%209%2C%2091022%20Castelvetrano%20TP!5e0!3m2!1sit!2sit!4v1699999999999!5m2!1sit!2sit"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Mappa G&B Wood - Castelvetrano"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
